import { getValidPrecedentNonMenuItemSelector } from '../../../services/validators/selectors/getValidPrecedentNonMenuItems';
import { Guid } from '../../store/model/guid';
import { AssertionException } from '../../utils/log';
import { isItemCompletedSelector } from '../tracking/services/CompletionService';
import { isItem, Item, MainItem } from '@ael/core-api';
import { createSelector } from 'reselect';

interface IsLockedDeps {
  restrictMenuOn: boolean;
  isItemCompleted: (item: Item) => boolean;
  getValidPrecedentNonMenuItems: (itemId: Guid) => MainItem[];
}

function calculateItemCompletion(precedentItem: MainItem, isItemCompleted: (item: Item) => boolean) {
  if (!isItem(precedentItem)) {
    throw AssertionException('isLocked method called for content item.');
  }

  const isCompleted = isItemCompleted(precedentItem);

  return isCompleted;
}

function isAnyPrecedentItemsIncomplete(
  getValidPrecedentNonMenuItems: (itemId: Guid) => MainItem[],
  itemId: string,
  isItemCompleted: (item: Item) => boolean
): boolean {
  const precedentItems = getValidPrecedentNonMenuItems(itemId);

  const precedentItemsCompletion = precedentItems.map((precedentItem) => {
    return calculateItemCompletion(precedentItem, isItemCompleted);
  });

  const anyPrecedentItemIsIncomplete = precedentItemsCompletion.some((result) => result === false);

  return anyPrecedentItemIsIncomplete;
}

export const calculateIsLocked = (
  { restrictMenuOn, isItemCompleted, getValidPrecedentNonMenuItems }: IsLockedDeps,
  itemId: Guid
): boolean => {
  if (!restrictMenuOn) {
    return false;
  }
  return isAnyPrecedentItemsIncomplete(getValidPrecedentNonMenuItems, itemId, isItemCompleted);
};

export const calculateIsLockedSelector = createSelector(
  getValidPrecedentNonMenuItemSelector,
  isItemCompletedSelector,
  (getValidPrecedentNonMenuItems, isItemCompleted) => (itemId: Guid, isLinearModeOn: boolean) =>
    calculateIsLocked(
      {
        restrictMenuOn: isLinearModeOn,
        getValidPrecedentNonMenuItems,
        isItemCompleted,
      },
      itemId
    )
);
